import { Box, CircularProgress, Collapse, Stack, Typography } from '@mui/material'
import { CreativeUploadFolder, useCreativeUploadPage } from './CreativeUpload.context'
import { DeleteOutline, DriveFileMoveOutlined, LoopOutlined, OpenInNew } from '@mui/icons-material'
import { ExpandedVisualPopupBase, ExpandedVisualSwitchDirection } from 'components/common/ExpandedVisualPopup'
import { FC, useEffect, useMemo, useRef } from 'react'
import { FileAction, FileState } from 'components/common/FileAPI'
import { GRAY_600, GRAY_900 } from 'constants/styling/theme'

import { ActionPage } from 'components/common/ActionPage'
import { AdditionalVisualsUpload } from '../AdditionalVisualsUpload.component'
import { CreativeUploadSubmitModal } from '../CreativeUploadSubmitModal.component'
import { DealState } from 'constants/deal'
import { EventTime } from 'components/common/EventTime'
import { FileAPIUpload } from 'components/common/FileAPIUpload'
import { FinishedFileStates } from 'components/common/FileAPI/fileUtils'
import { Label } from 'components/common/Label'
import { MUIButton } from 'components/common/MUIButton'
import { MUITooltip } from 'components/common/MUITooltip'
import { QueryStatus } from 'components/common/QueryStatus'
import { RequiredVisualsUpload } from '../RequiredVisualsUpload.component'
import { navigateBackOrHome } from 'utils/helpers'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { supportedImageFileTypes } from 'constants/misc'
import { useActionPopup, useOpenCreativeGuidelines } from 'utils/hooks'
import { useNavigate } from 'react-router-dom'
import { useTimezone } from 'components/contexts/timezone.context'
import { useTranslation } from 'react-i18next'

/**
 * Controller for CT upload flow
 * @example <CreativeUploadPageController />
 */
export const CreativeUploadPageController: FC = () => {
  const { t } = useTranslation(['ct_upload_page', 'image_upload_item', 'common', 'errors'])
  const navigate = useNavigate()
  const { showConfirm } = useActionPopup()
  const openGuidelines = useOpenCreativeGuidelines()

  const { userTimezone } = useTimezone()

  const {
    isSubmitOpen,
    assignmentQuery,
    visualsRequirementsQuery,
    requiredVisualAmount,
    uploadedRequiredVisualsAmount,
    uploadedAdditionalVisualsAmount,
    assignmentData,
    assignmentProduct,
    isAnyFileErroneous,
    isOverRequiredLimit,
    isAdditionalVisualsSectionOpen,
    droppedRequiredVisualsAmount,
    fileCtrl,
    expandedImageIndex,
    expandedImageFileEntry,
    expandedImageWebUrl,
    imagesForPopupPreview,
    setExpandedImageIndex,
    setIsSubmitOpen,
  } = useCreativeUploadPage()

  // Warn about uploads in progress while leaving the browser tab (same guard as the galleries)
  useEffect(() => {
    if (!fileCtrl.isAnyUploadRunning) return

    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', beforeUnloadHandler)
    return () => window.removeEventListener('beforeunload', beforeUnloadHandler)
  }, [fileCtrl.isAnyUploadRunning])

  const isLoading = useMemo(
    () => assignmentQuery.isPending || visualsRequirementsQuery.isPending,
    [assignmentQuery.isPending, visualsRequirementsQuery.isPending]
  )

  const isOrderCancelled = assignmentData?.orderState === DealState.CANCELLED

  // A failed listing leaves the grid empty, which is indistinguishable from "nothing uploaded yet" -- the exact bug this page had before ISSUE-3455
  const failedQuery = assignmentQuery.isError ? assignmentQuery : visualsRequirementsQuery.isError ? visualsRequirementsQuery : undefined

  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <ActionPage
      id="ct-upload-page"
      title={t('title')}
      onClose={async () => {
        if (!(await showConfirm(t('exit_popup_text'), { title: t('exit_popup_title'), confirmDenyText: t('exit_popup_deny'), confirmAcceptText: t('exit_popup_accept') }))) return
        navigateBackOrHome(navigate)
      }}
      footerChildren={!isOrderCancelled && !failedQuery && (
        <Stack
          width="100%"
          height="100%"
          alignItems="center"
          justifyContent="center"
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            width="90%"
            maxWidth="1200px"
            gap={1}
          >
            <MUITooltip
              content={t('cannot_submit_additional_visuals')}
              enterDelay={0}
              leaveDelay={0}
              isOpen={!fileCtrl.isAnyUploadRunning && uploadedRequiredVisualsAmount < requiredVisualAmount && uploadedAdditionalVisualsAmount > 0}
            >
              <MUIButton
                onClick={() => setIsSubmitOpen(true)}
                disabled={fileCtrl.isAnyUploadRunning || !uploadedRequiredVisualsAmount || isOverRequiredLimit || isAnyFileErroneous || (uploadedRequiredVisualsAmount < requiredVisualAmount && uploadedAdditionalVisualsAmount > 0)}
              >
                {t('submit')}
              </MUIButton>
            </MUITooltip>
          </Stack>
        </Stack>
      )}
    >
      <Stack
        width="100%"
        height="100%"
        overflow="auto"
        position="relative"
        direction="row"
        justifyContent="center"
        alignItems="flex-start"
      >

        {isLoading &&
          <CircularProgress
            sx={{
              position: 'absolute',
              top: '50%',
              right: 'calc(50% - 25px)',
              color: GRAY_600,
            }}
            variant="indeterminate"
            size={50}
          />
        }

        {!isLoading && !!failedQuery &&
          <Stack alignItems="center" justifyContent="center" height="100%" width="90%" maxWidth="1200px" gap={2}>

            <QueryStatus query={failedQuery} showStates={['error']} />

            {/* The footer is hidden in this state, so without a retry a transient 5xx costs the CT the page until they reload */}
            <MUIButton variant="outlined" onClick={() => failedQuery.refetch()}>
              {t('image_upload_item:retry')}
            </MUIButton>

          </Stack>
        }

        {/* A 200 that fails AssignmentDTOIsCreativeDTO leaves every other branch false; the gallery redirects straight back here, so a blank page is a dead end */}
        {!isLoading && !failedQuery && !assignmentData &&
          <Stack alignItems="center" justifyContent="center" height="100%" gap={2}>

            <Typography variant="text-md" color={GRAY_900}>
              {t('errors:none')}
            </Typography>

          </Stack>
        }

        {!isLoading && !failedQuery && isOrderCancelled &&
          <Stack alignItems="center" justifyContent="center" height="100%" gap={2}>

            <Typography variant="text-md" color={GRAY_900}>
              {t('order_cancelled_message')}
            </Typography>

          </Stack>
        }

        {!isLoading && !failedQuery && !!assignmentData && !isOrderCancelled &&
          <Stack
            pt={4}
            pb={4}
            width="90%"
            maxWidth="1200px"
            direction="row"
            alignItems="flex-start"
            gap={4}
          >

            {/* Main view */}
            <Stack
              direction="column"
              gap={6}
              flex="1 1 50rem"
              // A flex item never shrinks below its content's min width by default, so one wide child would push the assignment column off screen
              minWidth={0}
            >

              <RequiredVisualsUpload />

              <Collapse in={isAdditionalVisualsSectionOpen}>
                <AdditionalVisualsUpload />
              </Collapse>

            </Stack>

            {/* Assignment info */}
            <SectionedBorderBox
              flex="0 0 30rem"
              title={t('about_assignment')}
            >
              <Stack gap={1.6}>

                <Stack gap={.4}>

                  <Label text={t('assignment_id')} />
                  <Label text={assignmentData.id} color={GRAY_900} />
                </Stack>

                <Stack gap={.4}>

                  <Label text={t('address')} />
                  <Label text={assignmentData.address} color={GRAY_900} />

                </Stack>

                {!!assignmentData.shootingStartDateTime &&
                  <Stack gap={.4}>

                    <Label text={t('date_time')} />
                    <EventTime
                      eventStart={assignmentData.shootingStartDateTime}
                      eventTimezone={assignmentData.timezone}
                      userTimezone={userTimezone}
                    />

                  </Stack>
                }

                {!!assignmentData?.creativeInstructionsUrl &&
                  <MUIButton
                    type="secondaryBorder"
                    startIcon={<OpenInNew fontSize="large" />}
                    onClick={() => assignmentData && openGuidelines.open({ productKind: assignmentProduct?.kind, creativeInstructionsPairingId: assignmentData.creativeInstructionsPairingId, legacyExternalUrl: assignmentData.creativeInstructionsUrl })}
                  >
                    {t('mandatory_instructions')}
                  </MUIButton>
                }

              </Stack>
            </SectionedBorderBox>

          </Stack>
        }

        <CreativeUploadSubmitModal isOpen={isSubmitOpen} onClose={() => setIsSubmitOpen(false)} />

      </Stack>

      <ExpandedVisualPopupBase
        isOpen={expandedImageIndex !== undefined}
        onClose={() => setExpandedImageIndex(undefined)}
        switchExpandedVisual={(direction) => setExpandedImageIndex(prev => {
          const delta = direction === ExpandedVisualSwitchDirection.LEFT ? -1 : 1
          const next = ((prev ?? 0) + delta) % (imagesForPopupPreview.length)

          if (next < 0) return next + imagesForPopupPreview.length
          else return next
        })}
        filename={expandedImageFileEntry?.originalFilename ?? expandedImageFileEntry?.gcFilename ?? ''}
        isLoading={expandedImageFileEntry?.state === FileState.RUNNING}
        signedUrl={expandedImageWebUrl ?? expandedImageFileEntry?.displayUrl ?? undefined}
        label={expandedImageFileEntry ? t(`enums:ct_upload_file_folder.${expandedImageFileEntry.tag}`) : undefined}
        footerActionsSlot={!!expandedImageFileEntry &&
          <>
            <Box display="none">
              <FileAPIUpload
                ref={inputRef}
                acceptedFileTypes={supportedImageFileTypes}
                onCheckFileSize={(size) => size > 0}
                uploadHandler={(acceptedFiles) => fileCtrl.reUploadFile(acceptedFiles[0], expandedImageFileEntry.id, {
                  tag: expandedImageFileEntry.tag!,
                  onSuccess: () => {
                    fileCtrl.loadFiles([expandedImageFileEntry.id])
                  },
                })}
                files={[]}
                showFileListing={false}
                maxNumberOfFiles={1}
              />
            </Box>

            <MUIButton
              type="orangeSecondaryNoBorder"
              startIcon={<DeleteOutline fontSize="large" />}
              disabled={!FinishedFileStates.has(expandedImageFileEntry.state)}
              onClick={() => {
                if (imagesForPopupPreview.length === 1) setExpandedImageIndex(undefined)
                fileCtrl.deleteFiles([expandedImageFileEntry.id])
              }}
            >
              {t('common:Delete')}
            </MUIButton>

            <MUIButton
              type="secondaryNoBorder"
              startIcon={<LoopOutlined fontSize="large" />}
              disabled={expandedImageFileEntry?.action !== FileAction.LOAD || !FinishedFileStates.has(expandedImageFileEntry.state)}
              onClick={() => inputRef.current?.click()}
            >
              {t('image_upload_item:replace')}
            </MUIButton>

            {expandedImageFileEntry.tag === CreativeUploadFolder.REQUIRED_VISUALS && droppedRequiredVisualsAmount > requiredVisualAmount &&
              <MUIButton
                disabled={expandedImageFileEntry.action !== FileAction.LOAD || expandedImageFileEntry.state === FileState.RUNNING}
                startIcon={<DriveFileMoveOutlined fontSize="large" />}
                onClick={(e) => {
                  e.stopPropagation()
                  fileCtrl.editFile(expandedImageFileEntry.id, { tag: CreativeUploadFolder.ADDITIONAL_VISUALS })
                }}
              >
                {t('required_upload.move_to_additional')}
              </MUIButton>
            }

            {expandedImageFileEntry.tag === CreativeUploadFolder.ADDITIONAL_VISUALS && droppedRequiredVisualsAmount < requiredVisualAmount &&
              <MUIButton
                disabled={expandedImageFileEntry.action !== FileAction.LOAD || expandedImageFileEntry.state === FileState.RUNNING}
                startIcon={<DriveFileMoveOutlined fontSize="large" />}
                onClick={(e) => {
                  e.stopPropagation()
                  fileCtrl.editFile(expandedImageFileEntry.id, { tag: CreativeUploadFolder.REQUIRED_VISUALS })
                }}
              >
                {t('additional_upload.move_to_required')}
              </MUIButton>
            }

          </>
        }
      />

    </ActionPage>
  )
}
